// src/features/usage/hooks/useQuotas.ts
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  fetchQuotaList,
  fetchQuota,
  fetchAtRisk,
  createQuota,
  updateQuota,
  grantCredits,
  suspendQuota,
  type QuotaListResponse,
  type QuotaRow,
  type AtRiskResponse,
  type QuotaCreatePayload,
  type QuotaUpdatePayload,
  type CreditGrantPayload,
  type CreditGrantResult,
  type SuspendPayload,
} from '../api/quotaApi';

const QUOTA_KEY = ['quotas'] as const;

export function useQuotaList() {
  return useQuery<QuotaListResponse, Error>({
    queryKey:  [...QUOTA_KEY, 'list'],
    queryFn:   fetchQuotaList,
    staleTime: 30_000,
  });
}

export function useQuota(tenantId: number | null) {
  return useQuery<QuotaRow, Error>({
    queryKey:  [...QUOTA_KEY, tenantId],
    queryFn:   () => fetchQuota(tenantId!),
    enabled:   tenantId != null,
    staleTime: 30_000,
  });
}

export function useAtRisk(threshold?: number) {
  return useQuery<AtRiskResponse, Error>({
    queryKey:  [...QUOTA_KEY, 'at-risk', threshold],
    queryFn:   () => fetchAtRisk(threshold),
    staleTime: 30_000,
  });
}

export function useCreateQuota() {
  const qc = useQueryClient();
  return useMutation<QuotaRow, Error, QuotaCreatePayload>({
    mutationFn: createQuota,
    onSuccess:  () => { qc.invalidateQueries({ queryKey: QUOTA_KEY }); },
  });
}

export function useUpdateQuota() {
  const qc = useQueryClient();
  return useMutation<QuotaRow, Error, { tenantId: number; payload: QuotaUpdatePayload }>({
    mutationFn: ({ tenantId, payload }) => updateQuota(tenantId, payload),
    onSuccess:  () => { qc.invalidateQueries({ queryKey: QUOTA_KEY }); },
  });
}

export function useGrantCredits() {
  const qc = useQueryClient();
  return useMutation<CreditGrantResult, Error, { tenantId: number; payload: CreditGrantPayload }>({
    mutationFn: ({ tenantId, payload }) => grantCredits(tenantId, payload),
    onSuccess:  () => { qc.invalidateQueries({ queryKey: QUOTA_KEY }); },
  });
}

export function useSuspendQuota() {
  const qc = useQueryClient();
  return useMutation<QuotaRow, Error, { tenantId: number; payload: SuspendPayload }>({
    mutationFn: ({ tenantId, payload }) => suspendQuota(tenantId, payload),
    onSuccess:  () => { qc.invalidateQueries({ queryKey: QUOTA_KEY }); },
  });
}
